import {
  AiCategory,
  Band,
  BlobMediaKind,
  SubjectKind,
  TriggerReason,
} from './enums';

export interface ModerationEvent {
  contentModerationEventId: number;
  subjectKind: SubjectKind;
  fundraiserId: number | null;
  teamId: number | null;
  collectionId: number | null;
  blobPath: string | null;
  blobMediaKind: BlobMediaKind | null;
  linkedAccountId: number | null;
  linkedOrganizationId: number | null;
  triggerReason: TriggerReason;
  triggeredAt: string;
  band: Band;
  aiScore: number;
  aiCategory: AiCategory;
  aiConfidence: number;
  aiPromptVersion: string | null;
  aiModelVersion: string | null;
  autoDecidedAt: string | null;
  bodyHash: string | null;
  // Absolute URL to the live page (e.g. "https://cm.example.org/o/.../f/...").
  // Null on blob subjects (no public URL) and on any event where the backend
  // hasn't resolved it. Renders as a "View live page" external link in the
  // review header when present.
  pageUrlFull: string | null;
}

// The page snapshot is now a uniform list of elements. Each element carries
// its own AI flag, so flag highlighting no longer needs id-matching — we just
// read element.flagged. Image-role values are CDN paths (resolve via the
// asset URL helper); all other roles' values are display strings.
// Wire values are camelCase-first (same enum convention as the rest of the
// API — see CLAUDE.md). The API summary quoted them as PascalCase, but on the
// wire they arrive as heroImage / pageName / bodyText / etc.
export type ElementRole =
  | 'heroImage'
  | 'pageName'
  | 'url'
  | 'teamName'
  | 'organizationName'
  | 'metrics'
  | 'bodyText'
  | 'bodyImage'
  | 'blobImage';

export interface EventSnapshotElement {
  guid: string;
  role: ElementRole;
  // Can be "" or null on the wire (e.g. an image element with no asset) —
  // callers must guard before rendering.
  value: string | null;
  flagged: boolean;
}

export interface EventSnapshot {
  contentModerationEventId: number;
  pageNameSnapshot: string | null;
  aiReasoning: string | null;
  bodyTruncated: boolean;
  // Retained as typed scalars for the Block modal's collateral stats; the
  // formatted display string also lives in the Metrics-role element.
  raisedAmountSnapshot: number | null;
  donationCountSnapshot: number | null;
  elements: EventSnapshotElement[];
}

const IMAGE_ROLES: ReadonlySet<ElementRole> = new Set<ElementRole>([
  'heroImage',
  'bodyImage',
  'blobImage',
]);

export function isImageRole(role: ElementRole): boolean {
  return IMAGE_ROLES.has(role);
}

export function elementsByRole(
  elements: readonly EventSnapshotElement[],
  role: ElementRole,
): EventSnapshotElement[] {
  return elements.filter((e) => e.role === role);
}

export function firstElementByRole(
  elements: readonly EventSnapshotElement[],
  role: ElementRole,
): EventSnapshotElement | null {
  return elements.find((e) => e.role === role) ?? null;
}

export interface ActiveDecisionSummary {
  // The decision row id on the wire (used to drive POST /decision/{id}/reset).
  // Wire field is `id` — keep this matching the JSON shape verbatim so we
  // don't ship an undefined into the URL.
  id: number;
  decision: 'approve' | 'block';
  decidedAt: string;
  reviewerAccountId: number;
  reviewerName: string | null;
  disagreementReason: string | null;
  notes: string | null;
  agreedWithAi: boolean;
  // true when the AI auto-decided (auto-approve/auto-block) rather than a human
  // reviewer. Auto-decided events now carry an activeDecision too.
  isAutomated: boolean;
}

export interface LockSummary {
  reviewerAccountId: number;
  reviewerName: string | null;
  acquiredAt: string;
  lastHeartbeatAt: string;
}

export interface EventDetailResponse {
  event: ModerationEvent;
  snapshot: EventSnapshot;
  activeDecision: ActiveDecisionSummary | null;
  lock: LockSummary | null;
}

